/**
 * The treatment flows: which steps a cycle type has, in what order, and the copy
 * that describes them.
 *
 * Pure data and pure functions, extracted from `TreatmentProgressModal` so they
 * can be read without a React Native runtime. They could not be before, and the
 * cost of that was a second copy: `cycleHeaderSteps` hand-maintained its own
 * version of this graph purely because it could not import a component module,
 * and the two drifted — the header counted seven steps for IVF where the hub
 * showed eight.
 *
 * One source now, and one that a test can import.
 */

import type { CycleType } from "@louise/care/treatment-phases";
import type { Cycle } from "@src/packages/cycles/hooks/useCycles";
import {
  resolveConditionalCycleGraph,
  resolveCyclePlanFlow,
  type ConditionalCyclePhase,
  type PgtPlan,
} from "@src/packages/cycles/presenters/resolveConditionalCycleGraph";
import {
  insertRequiredStep,
  insertServerBackedSteps,
} from "./treatmentFlowGraph";
import type { TranslationFunction } from "@src/packages/i18n";

export interface TreatmentStepConfig {
  id: string;
  title: string;
  description: string;
  /** Prompt/question shown at top of expanded content */
  prompt?: string;
  /** Alternate prompt rendered when the user has entered a future
   *  procedureDate/transferDate — i.e. they are scheduling the step
   *  rather than logging that it happened. Falls back to `prompt` if
   *  not set, so steps without a future-date concept (like meds) need
   *  no change. */
  schedulingPrompt?: string;
  /** True when this step represents a clinic procedure with a fixed
   *  scheduling date (egg retrieval, transfer, insemination, sperm
   *  collection). Lab-outcome steps (fertilization, embryo_development,
   *  sperm-freezing analysis) and medication phases set this false/unset.
   *  Drives the Schedule vs Confirm & Save button + scheduling prompt
   *  affordance in StepWizardContent. */
  schedulable?: boolean;
  /** Skip link text for this step (if skippable) */
  skipText?: string;
  /** Whether this step can be skipped */
  skippable: boolean;
  /** Content type determines what UI to show */
  contentType:
    | "medications"
    | "procedure"
    | "monitoring"
    | "trigger"
    | "waiting";
  /**
   * True when the patient does not advance this step — its progress is derived
   * from the cycle itself.
   *
   * Every other step is something she ticks off, so the panel offers a
   * "Confirm & save" and the hub writes a phase when she presses it. The outcome
   * step is not hers to tick: it is done when a result exists and the cycle is
   * closed. Left unmarked it rendered an enabled button over an empty form —
   * `canComplete` is true precisely because the step declares no fields — and
   * pressing it created a `luteal_phase` row dated today, which then failed the
   * "luteal started after the transfer" guard and stopped the two-week wait ever
   * appearing for that cycle.
   *
   * Suppresses the advance and skip actions, and the prompt that goes with them.
   * `stepMayCreatePhase` in `usePhases.ts` refuses the write independently, so
   * the data is safe even if a caller reaches past the UI.
   */
  derivedProgress?: boolean;
  /** Whether this step supports logging a trigger shot */
  hasTriggerShot?: boolean;
  /** True when the step's results are a lab REPORT (e.g. semen analysis) that
   *  should be captured through the app's normal document/test-result upload
   *  flow rather than a bespoke inline form. When set, the hub renders a
   *  canonical "Add …results" row that opens that flow (structured values +
   *  the report are captured there). */
  resultsUpload?: boolean;
  /** Hex color for timeline node (optional, falls back to contentType-based color) */
  color?: string;
  /** Abbreviated label for timeline (e.g., "Stim"). Falls back to title. */
  shortTitle?: string;
  /** Additional fields for this step */
  fields?: {
    name: string;
    label: string;
    type:
      | "text"
      | "number"
      | "date"
      | "datetime"
      | "textarea"
      | "select"
      | "dropdown"
      | "dose"
      | "upload";
    placeholder?: string;
    options?: string[];
    /** Bitmask filter for medications dropdown — fetches meds from DB matching this type */
    medicationTypeFilter?: number;
    showIf?: { field: string; equals: string };
    /** When true, the step's Complete button stays disabled until this field
     * has a value. Number fields additionally require a value > 0. */
    required?: boolean;
  }[];
  /** Bitmask filter for medication type — when set, only shows matching meds in AddMedicationModal */
  medicationTypeFilter?: number;
  /**
   * Optional male-partner-perspective overrides. When an AMAB user views a
   * shared treatment (IVF/ICSI/IUI/FET) where the AFAB partner is the
   * primary patient, these strings replace the canonical copy and `fields`
   * are merged on top (same `name` ⇒ override).
   */
  partnerCopy?: {
    title?: string;
    description?: string;
    prompt?: string;
    skipText?: string;
    fields?: TreatmentStepConfig["fields"];
  };
}

/** Viewer perspective for step rendering. */
export type StepPerspective = "self" | "partner";

/**
 * Treatment flow configuration by cycle type.
 *
 * IMPORTANT: Step IDs match the API phase types from the Doctor Dashboard exactly.
 * This enables direct linking between patient app steps and clinic-managed phases.
 *
 * API phase types: pre_treatment, protocol, egg_retrieval, sperm_collection,
 *   fertilization, insemination, embryo_development, transfer, luteal_phase
 *
 * The patient-facing title may differ (e.g. "Ovarian Stimulation" vs "protocol")
 * but the ID is the source of truth for API linkage.
 */

/**
 * Reusable `sperm_collection` step config. Declared above TREATMENT_FLOWS so
 * ICSI, IUI, and the male-partner IVF/embryo_freezing inserts can all share
 * the same shape. The step ID is universal in the API phase mapping
 * (see `STEP_TO_PHASE_TYPE` in `usePhases.ts`).
 */
const SPERM_COLLECTION_STEP: TreatmentStepConfig = {
  id: "sperm_collection",
  title: "Sperm Collection",
  description: "Sperm sample collection and preparation",
  prompt: "Tell us about the sperm collection",
  schedulingPrompt: "When is the sperm collection scheduled?",
  skipText: "I don't have sperm collection details",
  skippable: true,
  contentType: "procedure",
  schedulable: true,
  fields: [
    {
      name: "spermSource",
      label: "Sperm Source",
      type: "select",
      options: ["partner", "donor"],
    },
    {
      name: "spermType",
      label: "Fresh or Frozen",
      type: "select",
      options: ["fresh", "frozen"],
    },
    {
      name: "collectionMethod",
      label: "Collection Type",
      type: "dropdown",
      options: [
        "ejaculation",
        "tese",
        "tesa",
        "pesa",
        "mesa",
        "electroejaculation",
      ],
    },
    {
      name: "preparationMethod",
      label: "Preparation",
      type: "select",
      options: [
        "none",
        "washed",
        "swim_up",
        "density_gradient",
        "dgc_swim_up",
        "microfluidics",
        "macs",
      ],
    },
    {
      name: "abstinencePeriod",
      label: "Days of Abstinence",
      type: "number",
      placeholder: "0",
    },
    { name: "collectionDate", label: "Collection Date", type: "datetime" },
    { name: "notes", label: "Notes", type: "textarea" },
  ],
};

const EMBRYO_DEVELOPMENT_STEP: TreatmentStepConfig = {
  id: "embryo_development",
  title: "Embryo Development",
  shortTitle: "Development",
  color: "#60A5FA",
  description: "Embryo growth, grading, and freezing decisions",
  prompt: "How are your embryos developing?",
  skipText: "I don't have embryo development details yet",
  skippable: true,
  contentType: "procedure",
  fields: [
    {
      name: "embryoGrade",
      label: "Top Embryo Grade",
      type: "dropdown",
      options: [
        "6AA",
        "5AA",
        "5AB",
        "5BA",
        "5BB",
        "4AA",
        "4AB",
        "4BA",
        "4BB",
        "3AA",
        "3AB",
        "3BA",
        "3BB",
        "2AA",
        "2AB",
        "2BA",
        "2BB",
        "1",
      ],
    },
    {
      name: "embryosDay3",
      label: "Embryos at Day 3",
      type: "number",
      placeholder: "0",
    },
    {
      name: "embryosDay5",
      label: "Embryos at Day 5",
      type: "number",
      placeholder: "0",
    },
    {
      name: "embryosFrozen",
      label: "Embryos Frozen",
      type: "number",
      placeholder: "0",
    },
    {
      name: "procedureDate",
      label: "Freeze Date & Time",
      type: "datetime",
    },
    {
      name: "storageLocation",
      label: "Storage Location",
      type: "text",
      placeholder: "Clinic / Bank / Other",
    },
    { name: "notes", label: "Notes", type: "textarea" },
  ],
};

/**
 * IUI procedure step shared by direct IUI and OI + IUI plans. OI only receives
 * this step when the server returned an insemination phase; the client must
 * not infer a procedure from `cycleType === "oi"`.
 */
const INSEMINATION_STEP: TreatmentStepConfig = {
  id: "insemination",
  title: "Insemination (IUI)",
  description: "Sperm is placed directly in the uterus",
  prompt: "How did your IUI procedure go?",
  schedulingPrompt: "When is your IUI scheduled?",
  skippable: false,
  contentType: "procedure",
  schedulable: true,
  fields: [
    {
      name: "procedureDate",
      label: "Procedure Date & Time",
      type: "datetime",
      required: true,
    },
    {
      name: "inseminationType",
      label: "IUI Type",
      type: "select",
      options: ["single", "double"],
    },
    {
      name: "catheterType",
      label: "Catheter Type",
      type: "select",
      options: ["straight", "curved"],
    },
    { name: "catheterBrand", label: "Catheter Brand", type: "text" },
    {
      name: "ultrasoundGuided",
      label: "Ultrasound Guided",
      type: "select",
      options: ["yes", "no"],
    },
    {
      name: "inseminationDuration",
      label: "Procedure Duration (minutes)",
      type: "number",
      placeholder: "0",
    },
    { name: "notes", label: "Notes", type: "textarea" },
  ],
  partnerCopy: {
    description: "Sperm is placed directly in your partner's uterus",
    prompt: "How did your partner's IUI procedure go?",
  },
};

/**
 * Embryo genetic testing (PGT) is persisted as a typed genetics result on
 * the embryo-development phase. Structured embryo reports and provenance are
 * tracked separately because they need embryo-level clinical records.
 */
const PGT_STEP: TreatmentStepConfig = {
  id: "pgt_testing",
  title: "Genetic Testing (PGT)",
  shortTitle: "PGT",
  color: "#A78BFA",
  description: "Genetic testing of your embryos before transfer",
  prompt: "Tell us about your PGT results",
  skipText: "We're not doing PGT this cycle",
  skippable: true,
  contentType: "procedure",
  fields: [
    { name: "pgtResultDocId", label: "PGT results (PDF)", type: "upload" },
    {
      name: "euploidCount",
      label: "Euploid (normal) embryos",
      type: "number",
      placeholder: "0",
    },
    {
      name: "pgtNotes",
      label: "Notes",
      type: "textarea",
      placeholder: "Anything to flag from your PGT report...",
    },
  ],
};

/**
 * The terminal step for every flow that ends in a result: the two-week wait, and
 * the result recorded against it.
 *
 * This state has always existed on screen — it was rendered on the `luteal_phase`
 * tab, which meant the luteal support content, the progesterone she is still
 * taking, was replaced by the wait for as long as the wait lasted and never came
 * back once a result landed. They are two different things and now they are two
 * tabs.
 *
 * Shares the `luteal_phase` API phase rather than inventing one, the same way
 * `freezing`, `pgt_testing` and `stimulation` share theirs — see
 * `STEP_TO_PHASE_TYPE` in `usePhases.ts`. The result itself does not live on a
 * phase at all; it is the cycle's outcome record.
 */
const OUTCOME_STEP: TreatmentStepConfig = {
  id: "outcome",
  title: "Outcome",
  description: "Your result for this cycle",
  prompt: "What was the result of this cycle?",
  skippable: false,
  contentType: "waiting",
  // The result is recorded through the beta flow and the cycle is ended by
  // closing it. Neither is this step's own form to collect — which is why it
  // offers no fields and no advance action, and delegates to that flow through
  // `renderOutcomeAction` instead, the same way egg retrieval does.
  derivedProgress: true,
  partnerCopy: {
    description: "The result of your partner's cycle",
    prompt: "What was the result of this cycle?",
  },
};

export const TREATMENT_FLOWS: Record<CycleType, TreatmentStepConfig[]> = {
  // ---------------------------------------------------------------------------
  // IVF: pre_treatment → protocol → egg_retrieval → fertilization
  //      → embryo_development → transfer → luteal_phase
  // ---------------------------------------------------------------------------
  ivf: [
    {
      id: "pre_treatment",
      title: "Pre-treatment",
      description: "Birth control or priming medications before stimulation",
      prompt:
        "Did you take birth control or priming medications before stimulation?",
      skipText: "I didn't have pre-treatment this cycle",
      skippable: true,
      contentType: "medications",
      medicationTypeFilter: 32,
      partnerCopy: {
        description:
          "Supplements or medications to support sperm quality before collection",
        prompt:
          "Are you taking any supplements or medications before collection?",
      },
    },
    {
      id: "protocol",
      title: "Ovarian Stimulation",
      description:
        "Medication phase to stimulate egg development, including trigger shot",
      prompt: "What medications did you take during stimulation?",
      // Ovarian stimulation is mandatory for this cycle type — you can't reach
      // retrieval without it, so it is not skippable. A cycle that stops here is
      // a cancellation, which the cycle close-out flow handles, not a step skip.
      skippable: false,
      contentType: "medications",
      hasTriggerShot: true,
      partnerCopy: {
        description:
          "Medication phase your partner is taking to stimulate egg development, including trigger shot",
        prompt: "What medications is your partner taking during stimulation?",
      },
    },
    {
      id: "egg_retrieval",
      title: "Egg Retrieval",
      description: "Egg collection procedure",
      prompt: "How did your egg retrieval go?",
      schedulingPrompt: "When is your egg retrieval scheduled?",
      skippable: false,
      contentType: "procedure",
      schedulable: true,
      fields: [
        {
          name: "procedureDate",
          label: "Procedure Date & Time",
          type: "datetime",
          required: true,
        },
        {
          name: "eggsCollected",
          label: "Eggs Collected",
          type: "number",
          placeholder: "0",
          required: true,
        },
        {
          name: "notes",
          label: "Notes",
          type: "textarea",
          placeholder: "Quality notes...",
        },
      ],
      partnerCopy: {
        description: "Your partner's egg collection procedure",
        prompt: "How did your partner's egg retrieval go?",
        fields: [
          {
            name: "eggsCollected",
            label: "Eggs partner retrieved",
            type: "number",
            placeholder: "0",
          },
        ],
      },
    },
    {
      id: "fertilization",
      title: "Fertilization",
      description: "How your eggs were fertilized",
      prompt: "What are your fertilization results?",
      skipText: "I don't have fertilization details yet",
      skippable: true,
      contentType: "procedure",
      fields: [
        {
          name: "spermSource",
          label: "Sperm Source",
          type: "select",
          options: ["partner", "donor"],
          required: true,
        },
        {
          name: "spermType",
          label: "Fresh or Frozen",
          type: "select",
          options: ["fresh", "frozen"],
          required: true,
        },
        {
          name: "fertilizationMethod",
          label: "Fertilization Method",
          type: "select",
          options: ["ivf", "icsi"],
          required: true,
        },
        {
          name: "eggsFertilized",
          label: "Eggs Fertilized (2PN)",
          type: "number",
          placeholder: "0",
          required: true,
        },
        { name: "notes", label: "Notes", type: "textarea" },
      ],
      partnerCopy: {
        skipText: "We don't have fertilization details yet",
      },
    },
    {
      id: "embryo_development",
      title: "Embryo Development",
      description: "Embryo growth, grading, and freezing decisions",
      prompt: "How are your embryos developing?",
      skipText: "I don't have embryo details yet",
      skippable: true,
      contentType: "procedure",
      fields: [
        {
          name: "embryoGrade",
          label: "Top Embryo Grade",
          type: "dropdown",
          options: [
            "6AA",
            "5AA",
            "5AB",
            "5BA",
            "5BB",
            "4AA",
            "4AB",
            "4BA",
            "4BB",
            "3AA",
            "3AB",
            "3BA",
            "3BB",
            "2AA",
            "2AB",
            "2BA",
            "2BB",
            "1",
          ],
        },
        {
          name: "embryosFrozen",
          label: "Embryos Frozen",
          type: "number",
          placeholder: "0",
        },
        {
          name: "embryosDay3",
          label: "Embryos at Day 3",
          type: "number",
          placeholder: "0",
        },
        {
          name: "embryosDay5",
          label: "Embryos at Day 5",
          type: "number",
          placeholder: "0",
        },
      ],
      partnerCopy: {
        skipText: "We don't have embryo details yet",
      },
    },
    {
      id: "transfer",
      title: "Embryo Transfer",
      description: "Embryo transfer to the uterus",
      prompt: "Tell us about your embryo transfer",
      schedulingPrompt: "When is your transfer scheduled?",
      skipText: "I'm freezing all embryos this cycle",
      skippable: true,
      contentType: "procedure",
      schedulable: true,
      fields: [
        {
          name: "transferDate",
          label: "Transfer Date",
          type: "datetime",
          required: true,
        },
        {
          name: "embryosTransferred",
          label: "Embryos Transferred",
          type: "number",
          placeholder: "0",
          required: true,
        },
        {
          name: "embryoType",
          label: "Embryo Type",
          type: "select",
          options: ["fresh", "frozen"],
        },
        {
          name: "notes",
          label: "Notes",
          type: "textarea",
        },
      ],
      partnerCopy: {
        description: "Your partner's embryo transfer to the uterus",
        prompt: "Tell us about your partner's embryo transfer",
        skipText: "We're freezing all embryos this cycle",
      },
    },
    {
      id: "luteal_phase",
      title: "Luteal Support",
      description: "Post-transfer support medications",
      prompt: "What support medications are you taking?",
      skippable: false,
      contentType: "medications",
      medicationTypeFilter: 16,
      partnerCopy: {
        title: "Partner's Luteal Support",
        description: "Post-transfer support medications your partner is taking",
        prompt: "What support medications is your partner taking?",
      },
    },
  ],

  // ---------------------------------------------------------------------------
  // ICSI: pre_treatment → protocol → egg_retrieval → sperm_collection
  //       → fertilization → embryo_development → transfer → luteal_phase
  // ---------------------------------------------------------------------------
  icsi: [
    {
      id: "pre_treatment",
      title: "Pre-treatment",
      description: "Birth control or priming medications before stimulation",
      prompt: "Did you take birth control or priming medications?",
      skipText: "I didn't have pre-treatment this cycle",
      skippable: true,
      contentType: "medications",
      medicationTypeFilter: 32,
      partnerCopy: {
        description:
          "Supplements or medications to support sperm quality before collection",
        prompt:
          "Are you taking any supplements or medications before collection?",
      },
    },
    {
      id: "protocol",
      title: "Ovarian Stimulation",
      description:
        "Medication phase to stimulate egg development, including trigger shot",
      prompt: "What medications did you take during stimulation?",
      // Ovarian stimulation is mandatory for this cycle type — you can't reach
      // retrieval without it, so it is not skippable. A cycle that stops here is
      // a cancellation, which the cycle close-out flow handles, not a step skip.
      skippable: false,
      contentType: "medications",
      hasTriggerShot: true,
      partnerCopy: {
        description:
          "Medication phase your partner is taking to stimulate egg development, including trigger shot",
        prompt: "What medications is your partner taking during stimulation?",
      },
    },
    {
      id: "egg_retrieval",
      title: "Egg Retrieval",
      description: "Egg collection procedure",
      prompt: "How did your egg retrieval go?",
      schedulingPrompt: "When is your egg retrieval scheduled?",
      skippable: false,
      contentType: "procedure",
      schedulable: true,
      fields: [
        {
          name: "procedureDate",
          label: "Procedure Date & Time",
          type: "datetime",
          required: true,
        },
        {
          name: "eggsCollected",
          label: "Eggs Collected",
          type: "number",
          placeholder: "0",
          required: true,
        },
        { name: "notes", label: "Notes", type: "textarea" },
      ],
      partnerCopy: {
        description: "Your partner's egg collection procedure",
        prompt: "How did your partner's egg retrieval go?",
        fields: [
          {
            name: "eggsCollected",
            label: "Eggs partner retrieved",
            type: "number",
            placeholder: "0",
          },
        ],
      },
    },
    SPERM_COLLECTION_STEP,
    {
      id: "fertilization",
      title: "Fertilization (ICSI)",
      description: "Intracytoplasmic sperm injection results",
      prompt: "What are your ICSI fertilization results?",
      skipText: "I don't have fertilization details yet",
      skippable: true,
      contentType: "procedure",
      fields: [
        {
          name: "spermSource",
          label: "Sperm Source",
          type: "select",
          options: ["partner", "donor"],
          required: true,
        },
        {
          name: "spermType",
          label: "Fresh or Frozen",
          type: "select",
          options: ["fresh", "frozen"],
          required: true,
        },
        {
          name: "eggsInjected",
          label: "Eggs Injected (ICSI)",
          type: "number",
          placeholder: "0",
          required: true,
        },
        {
          name: "eggsFertilized",
          label: "Eggs Fertilized (2PN)",
          type: "number",
          placeholder: "0",
          required: true,
        },
        { name: "notes", label: "Notes", type: "textarea" },
      ],
      partnerCopy: {
        skipText: "We don't have fertilization details yet",
      },
    },
    {
      id: "embryo_development",
      title: "Embryo Development",
      description: "Embryo growth, grading, and freezing decisions",
      prompt: "How are your embryos developing?",
      skipText: "I don't have embryo details yet",
      skippable: true,
      contentType: "procedure",
      fields: [
        {
          name: "embryoGrade",
          label: "Top Embryo Grade",
          type: "dropdown",
          options: [
            "6AA",
            "5AA",
            "5AB",
            "5BA",
            "5BB",
            "4AA",
            "4AB",
            "4BA",
            "4BB",
            "3AA",
            "3AB",
            "3BA",
            "3BB",
            "2AA",
            "2AB",
            "2BA",
            "2BB",
            "1",
          ],
        },
        {
          name: "embryosFrozen",
          label: "Embryos Frozen",
          type: "number",
          placeholder: "0",
        },
        {
          name: "embryosDay3",
          label: "Embryos at Day 3",
          type: "number",
          placeholder: "0",
        },
        {
          name: "embryosDay5",
          label: "Embryos at Day 5",
          type: "number",
          placeholder: "0",
        },
      ],
      partnerCopy: {
        skipText: "We don't have embryo details yet",
      },
    },
    {
      id: "transfer",
      title: "Embryo Transfer",
      description: "Embryo transfer to the uterus",
      prompt: "Tell us about your embryo transfer",
      schedulingPrompt: "When is your transfer scheduled?",
      skipText: "I'm freezing all embryos this cycle",
      skippable: true,
      contentType: "procedure",
      schedulable: true,
      fields: [
        {
          name: "transferDate",
          label: "Transfer Date",
          type: "datetime",
          required: true,
        },
        {
          name: "embryosTransferred",
          label: "Embryos Transferred",
          type: "number",
          placeholder: "0",
          required: true,
        },
        {
          name: "embryoType",
          label: "Embryo Type",
          type: "select",
          options: ["fresh", "frozen"],
        },
        { name: "notes", label: "Notes", type: "textarea" },
      ],
      partnerCopy: {
        description: "Your partner's embryo transfer to the uterus",
        prompt: "Tell us about your partner's embryo transfer",
        skipText: "We're freezing all embryos this cycle",
      },
    },
    {
      id: "luteal_phase",
      title: "Luteal Support",
      description: "Post-transfer support medications",
      prompt: "What support medications are you taking?",
      skippable: false,
      contentType: "medications",
      medicationTypeFilter: 16,
      partnerCopy: {
        title: "Partner's Luteal Support",
        description: "Post-transfer support medications your partner is taking",
        prompt: "What support medications is your partner taking?",
      },
    },
  ],

  // ---------------------------------------------------------------------------
  // IUI: pre_treatment → protocol → sperm_collection → insemination → luteal_phase
  // ---------------------------------------------------------------------------
  iui: [
    {
      id: "pre_treatment",
      title: "Pre-treatment",
      description: "Preparation before IUI cycle",
      prompt: "Did you take any medications before your IUI cycle?",
      skipText: "I didn't have pre-treatment this cycle",
      skippable: true,
      contentType: "medications",
      medicationTypeFilter: 32,
      partnerCopy: {
        description:
          "Supplements or medications to support sperm quality before collection",
        prompt:
          "Are you taking any supplements or medications before collection?",
      },
    },
    {
      id: "protocol",
      title: "Ovarian Stimulation",
      description:
        "Medication to support egg development, including trigger shot",
      prompt: "What medications did you take during stimulation?",
      skippable: false,
      contentType: "medications",
      hasTriggerShot: true,
      partnerCopy: {
        description:
          "Medication your partner is taking to support egg development, including trigger shot",
        prompt: "What medications is your partner taking during stimulation?",
      },
    },
    SPERM_COLLECTION_STEP,
    INSEMINATION_STEP,
    {
      id: "luteal_phase",
      title: "Luteal Support",
      description: "Post-insemination support",
      prompt: "Are you taking any support medications?",
      skipText: "I'm not taking luteal support",
      skippable: true,
      contentType: "medications",
      medicationTypeFilter: 16,
      partnerCopy: {
        title: "Partner's Luteal Support",
        description: "Post-insemination support your partner is taking",
        prompt: "Is your partner taking any support medications?",
        skipText: "My partner isn't taking luteal support",
      },
    },
  ],

  // ---------------------------------------------------------------------------
  // FET: pre_treatment → protocol → transfer → luteal_phase
  // ---------------------------------------------------------------------------
  fet: [
    {
      id: "pre_treatment",
      title: "Pre-treatment",
      description: "Preparation before transfer cycle",
      prompt: "Did you take any medications before your FET cycle?",
      skipText: "I didn't have pre-treatment this cycle",
      skippable: true,
      contentType: "medications",
      medicationTypeFilter: 32,
      partnerCopy: {
        description:
          "Supplements or medications to support sperm quality before collection",
        prompt:
          "Are you taking any supplements or medications before collection?",
      },
    },
    {
      id: "protocol",
      title: "Endometrial Preparation",
      shortTitle: "Lining",
      description: "Building up the uterine lining for transfer",
      prompt: "What medications are you taking to prepare your lining?",
      skippable: false,
      contentType: "medications",
      partnerCopy: {
        description:
          "Medications your partner is taking to build up her uterine lining for transfer",
        prompt:
          "What medications is your partner taking to prepare her lining?",
      },
    },
    {
      id: "transfer",
      title: "Embryo Transfer",
      description: "Frozen embryo transfer to the uterus",
      prompt: "Tell us about your embryo transfer",
      schedulingPrompt: "When is your transfer scheduled?",
      skippable: false,
      contentType: "procedure",
      schedulable: true,
      fields: [
        {
          name: "transferDate",
          label: "Transfer Date",
          type: "datetime",
          required: true,
        },
        {
          name: "embryosTransferred",
          label: "Embryos Transferred",
          type: "number",
          placeholder: "0",
          required: true,
        },
        {
          name: "embryoType",
          label: "Embryo Type",
          type: "select",
          options: ["fresh", "frozen"],
        },
        { name: "notes", label: "Notes", type: "textarea" },
      ],
      partnerCopy: {
        description: "Frozen embryo transfer to your partner's uterus",
        prompt: "Tell us about your partner's embryo transfer",
      },
    },
    {
      id: "luteal_phase",
      title: "Luteal Support",
      description: "Post-transfer support medications",
      prompt: "What support medications are you taking?",
      skippable: false,
      contentType: "medications",
      medicationTypeFilter: 16,
      partnerCopy: {
        title: "Partner's Luteal Support",
        description: "Post-transfer support medications your partner is taking",
        prompt: "What support medications is your partner taking?",
      },
    },
  ],

  // ---------------------------------------------------------------------------
  // OI: pre_treatment → protocol → luteal_phase
  // ---------------------------------------------------------------------------
  oi: [
    {
      id: "pre_treatment",
      title: "Pre-treatment",
      description: "Baseline tests and preparation",
      prompt: "Did you take any pre-treatment medications?",
      skipText: "I didn't have pre-treatment this cycle",
      skippable: true,
      contentType: "medications",
      medicationTypeFilter: 32,
    },
    {
      id: "protocol",
      title: "Ovulation Induction",
      shortTitle: "Induction",
      description:
        "Medication to stimulate ovulation, including trigger shot and timing",
      prompt: "What medications are you taking for ovulation induction?",
      skippable: true,
      contentType: "medications",
      hasTriggerShot: true,
      // The protocol phase does not yet have a structured OI/TI event field.
      // Keep notes only; #2417 owns the durable event contract.
      fields: [{ name: "notes", label: "Notes", type: "textarea" }],
    },
    {
      id: "luteal_phase",
      title: "Luteal Support",
      description: "Post-ovulation support and waiting period",
      prompt: "Are you taking any support medications?",
      skipText: "I'm not taking luteal support",
      skippable: true,
      contentType: "medications",
      medicationTypeFilter: 16,
    },
  ],

  // ---------------------------------------------------------------------------
  // Egg Freezing: pre_treatment → stimulation → trigger_shot → egg_retrieval → freezing
  // 5 patient-facing steps (API stores whatever stepProgress array we send)
  // ---------------------------------------------------------------------------
  egg_freezing: [
    {
      id: "pre_treatment",
      title: "Pre-treatment",
      shortTitle: "Pre-treatment",
      color: "#94a3b8",
      description: "Birth control or priming medications",
      prompt: "Did you take birth control or priming medications?",
      skipText: "I didn't have pre-treatment this cycle",
      skippable: true,
      contentType: "medications",
      medicationTypeFilter: 32,
    },
    {
      id: "stimulation",
      title: "Stimulation & Monitoring",
      shortTitle: "Stimulation",
      color: "#C4B5FD",
      description: "Medication phase to stimulate egg development",
      prompt: "What medications did you take during stimulation?",
      // Ovarian stimulation is mandatory for this cycle type — you can't reach
      // retrieval without it, so it is not skippable. A cycle that stops here is
      // a cancellation, which the cycle close-out flow handles, not a step skip.
      skippable: false,
      contentType: "medications",
    },
    {
      id: "trigger_shot",
      title: "Trigger Shot",
      shortTitle: "Trigger",
      color: "#fbbf24",
      description: "Final injection to trigger egg maturation before retrieval",
      prompt: "Log your trigger shot the same way as your other medications.",
      skippable: false,
      // Use the medication catalog (filtered to trigger meds) so the user
      // gets the proper dose/time picker and the trigger ends up in the
      // medication log alongside the rest — no inline free-text dose.
      contentType: "medications",
      hasTriggerShot: true,
      medicationTypeFilter: 8,
    },
    {
      id: "egg_retrieval",
      title: "Egg Retrieval",
      shortTitle: "Retrieval",
      color: "#f472b6",
      description: "Egg collection procedure",
      prompt: "How did your egg retrieval go?",
      schedulingPrompt: "When is your egg retrieval scheduled?",
      skippable: false,
      contentType: "procedure",
      schedulable: true,
      fields: [
        {
          name: "procedureDate",
          label: "Procedure Date & Time",
          type: "datetime",
          required: true,
        },
        {
          name: "eggsCollected",
          label: "Eggs Collected",
          type: "number",
          placeholder: "0",
          required: true,
        },
        {
          name: "notes",
          label: "Notes",
          type: "textarea",
          placeholder: "How did it go?",
        },
      ],
    },
    {
      id: "freezing",
      title: "Outcome",
      shortTitle: "Outcome",
      color: "#4ade80",
      description: "Mature eggs, eggs frozen, and lab notes",
      prompt: "What were your freezing outcomes?",
      skippable: false,
      contentType: "procedure",
      fields: [
        {
          name: "matureEggs",
          label: "Mature Eggs (MII)",
          type: "number",
          placeholder: "0",
        },
        {
          name: "eggsFrozen",
          label: "Eggs Frozen",
          type: "number",
          placeholder: "0",
        },
        {
          name: "notes",
          label: "Notes",
          type: "textarea",
          placeholder: "Quality notes...",
        },
      ],
    },
  ],

  // ---------------------------------------------------------------------------
  // Embryo Freezing: pre_treatment → protocol → egg_retrieval →
  // sperm_collection → fertilization → embryo_development.
  // The last two conditional phases are supplied by the cycle API.
  // ---------------------------------------------------------------------------
  embryo_freezing: [
    {
      id: "pre_treatment",
      title: "Pre-treatment",
      description: "Birth control or priming medications",
      prompt: "Did you take birth control or priming medications?",
      skipText: "I didn't have pre-treatment this cycle",
      skippable: true,
      contentType: "medications",
      medicationTypeFilter: 32,
      partnerCopy: {
        description:
          "Supplements or medications to support sperm quality before collection",
        prompt:
          "Are you taking any supplements or medications before collection?",
      },
    },
    {
      id: "protocol",
      title: "Ovarian Stimulation",
      description:
        "Medication to stimulate egg development, including trigger shot",
      prompt: "What medications did you take during stimulation?",
      // Ovarian stimulation is mandatory for this cycle type — you can't reach
      // retrieval without it, so it is not skippable. A cycle that stops here is
      // a cancellation, which the cycle close-out flow handles, not a step skip.
      skippable: false,
      contentType: "medications",
      hasTriggerShot: true,
      partnerCopy: {
        description:
          "Medication your partner is taking to stimulate egg development, including trigger shot",
        prompt: "What medications is your partner taking during stimulation?",
      },
    },
    {
      id: "egg_retrieval",
      title: "Egg Retrieval",
      description: "Egg collection procedure",
      prompt: "How did your egg retrieval go?",
      schedulingPrompt: "When is your egg retrieval scheduled?",
      skippable: false,
      contentType: "procedure",
      schedulable: true,
      fields: [
        {
          name: "procedureDate",
          label: "Procedure Date & Time",
          type: "datetime",
          required: true,
        },
        {
          name: "eggsCollected",
          label: "Eggs Collected",
          type: "number",
          placeholder: "0",
          required: true,
        },
        { name: "notes", label: "Notes", type: "textarea" },
      ],
      partnerCopy: {
        description: "Your partner's egg collection procedure",
        prompt: "How did your partner's egg retrieval go?",
        fields: [
          {
            name: "eggsCollected",
            label: "Eggs partner retrieved",
            type: "number",
            placeholder: "0",
          },
        ],
      },
    },
    {
      id: "fertilization",
      title: "Fertilization",
      description: "Fertilization method and lab results",
      prompt: "How were your eggs fertilized?",
      skipText: "I don't have fertilization details yet",
      skippable: true,
      contentType: "procedure",
      partnerCopy: {
        skipText: "We don't have fertilization details yet",
      },
      fields: [
        {
          name: "spermSource",
          label: "Sperm Source",
          type: "select",
          options: ["partner", "donor"],
        },
        {
          name: "fertilizationMethod",
          label: "Method",
          type: "select",
          options: ["ivf", "icsi"],
        },
        {
          name: "eggsFertilized",
          label: "Eggs Fertilized (2PN)",
          type: "number",
          placeholder: "0",
        },
        { name: "notes", label: "Notes", type: "textarea" },
      ],
    },
  ],
  sperm_freezing: [
    {
      id: "pre_treatment",
      title: "Pre-collection prep",
      shortTitle: "Pre-treatment",
      color: "#94a3b8",
      description: "Medications or supplements before your collection",
      prompt:
        "Are you taking any medications or supplements before your collection?",
      skipText: "I'm not taking anything",
      skippable: true,
      contentType: "medications",
    },
    {
      id: "screening",
      title: "Screening",
      shortTitle: "Screening",
      color: "#7DD3FC",
      description:
        "Screening results before freezing; add infectious-disease, genetic, or carrier documents",
      prompt: "Upload any screening documents and add notes for your clinic",
      skippable: false,
      contentType: "procedure",
      fields: [
        {
          name: "screeningDocumentId",
          label: "Upload screening document",
          type: "upload",
        },
        {
          name: "stdScreeningDate",
          label: "STD / infectious-disease screening date",
          type: "date",
        },
        {
          name: "stdResultNotes",
          label: "STD / infectious-disease screening notes",
          type: "textarea",
          placeholder: "Add the result or any notes about the screening...",
        },
        {
          name: "geneticScreeningDate",
          label: "Genetic / carrier screening date",
          type: "date",
        },
        {
          name: "geneticResultNotes",
          label: "Genetic / carrier screening notes",
          type: "textarea",
          placeholder: "Add the result or any notes about the screening...",
        },
        {
          name: "notes",
          label: "Additional Notes",
          type: "textarea",
          placeholder: "Anything else to flag for your clinic...",
        },
      ],
    },
    {
      id: "sperm_collection",
      title: "Sperm Collection",
      shortTitle: "Collection",
      color: "#A78BFA",
      description: "Sample collection at the clinic or laboratory",
      prompt: "Tell us about the collection",
      schedulingPrompt: "When is the sperm collection scheduled?",
      skippable: false,
      contentType: "procedure",
      schedulable: true,
      fields: [
        {
          name: "collectionDate",
          label: "Collection Date & Time",
          type: "datetime",
        },
        {
          name: "abstinenceDays",
          label: "Days of Abstinence",
          type: "number",
          placeholder: "e.g. 3",
        },
        {
          name: "spermType",
          label: "Fresh or Frozen",
          type: "select",
          options: ["fresh", "frozen"],
        },
        {
          name: "notes",
          label: "Notes",
          type: "textarea",
          placeholder: "Quality, conditions, anything notable...",
        },
      ],
    },
    {
      id: "analysis",
      title: "Semen Analysis",
      shortTitle: "Analysis",
      color: "#f472b6",
      description: "Sperm count, motility, morphology, and volume",
      prompt:
        "Upload your analysis report to auto-fill, or enter the values manually.",
      skippable: false,
      contentType: "procedure",
      // Semen analysis is a lab report — capture it through the normal
      // document/test-result upload flow (which parses the structured values)
      // instead of a bespoke inline form.
      resultsUpload: true,
      fields: [
        {
          name: "analysisResultDocId",
          label: "Analysis Report (PDF)",
          type: "upload",
        },
        {
          name: "concentration",
          label: "Concentration (M/mL)",
          type: "number",
          placeholder: "0",
        },
        {
          name: "totalCount",
          label: "Total Count (M)",
          type: "number",
          placeholder: "0",
        },
        {
          name: "motilityPercent",
          label: "Motility (%)",
          type: "number",
          placeholder: "0",
        },
        {
          name: "morphologyPercent",
          label: "Normal Morphology (%)",
          type: "number",
          placeholder: "0",
        },
        {
          name: "volumeMl",
          label: "Volume (mL)",
          type: "number",
          placeholder: "0",
        },
        { name: "notes", label: "Notes", type: "textarea" },
      ],
    },
    {
      id: "freezing",
      title: "Freezing",
      shortTitle: "Freezing",
      color: "#4ade80",
      description: "Vials frozen — storage details captured in Cryo Vault",
      prompt:
        "How many vials were frozen? You'll add storage location in your Cryo Vault.",
      skippable: false,
      contentType: "procedure",
      fields: [
        {
          name: "vialsFrozen",
          label: "Vials Frozen",
          type: "number",
          placeholder: "0",
        },
        {
          name: "freezeDate",
          label: "Freeze Date",
          type: "datetime",
        },
        {
          name: "notes",
          label: "Notes",
          type: "textarea",
          placeholder: "Anything to remember...",
        },
      ],
    },
  ],
};

/**
 * A translation with a literal fallback, so a missing key renders the English
 * that was written beside the flow rather than the key itself.
 *
 * Exported because the wizard still resolves option labels this way.
 */
export function tf(
  t: TranslationFunction,
  key: string,
  fallback: string,
): string {
  const value = t(key);
  return value === key ? fallback : value;
}

function tfOptional(
  t: TranslationFunction,
  key: string,
  fallback: string | undefined,
): string | undefined {
  if (fallback === undefined) return undefined;
  return tf(t, key, fallback);
}

function translateFields(
  t: TranslationFunction,
  base: string,
  fields: TreatmentStepConfig["fields"],
): TreatmentStepConfig["fields"] {
  if (!fields) return fields;
  return fields.map((f) => ({
    ...f,
    label: tf(t, `${base}.fields.${f.name}.label`, f.label),
    placeholder: tfOptional(
      t,
      `${base}.fields.${f.name}.placeholder`,
      f.placeholder,
    ),
  }));
}

function translateStep(
  step: TreatmentStepConfig,
  cycleType: CycleType,
  t: TranslationFunction,
): TreatmentStepConfig {
  const base = `care.treatment_flows.${cycleType}.${step.id}`;
  const partnerBase = `${base}.partner`;
  return {
    ...step,
    title: tf(t, `${base}.title`, step.title),
    description: tf(t, `${base}.description`, step.description),
    prompt: tfOptional(t, `${base}.prompt`, step.prompt),
    schedulingPrompt: tfOptional(
      t,
      `${base}.scheduling_prompt`,
      step.schedulingPrompt,
    ),
    skipText: tfOptional(t, `${base}.skip_text`, step.skipText),
    shortTitle: tfOptional(t, `${base}.short_title`, step.shortTitle),
    fields: translateFields(t, base, step.fields),
    partnerCopy: step.partnerCopy
      ? {
          title: tfOptional(t, `${partnerBase}.title`, step.partnerCopy.title),
          description: tfOptional(
            t,
            `${partnerBase}.description`,
            step.partnerCopy.description,
          ),
          prompt: tfOptional(
            t,
            `${partnerBase}.prompt`,
            step.partnerCopy.prompt,
          ),
          skipText: tfOptional(
            t,
            `${partnerBase}.skip_text`,
            step.partnerCopy.skipText,
          ),
          fields: translateFields(t, partnerBase, step.partnerCopy.fields),
        }
      : undefined,
  };
}

// =============================================================================
// Male-partner perspective layer
// =============================================================================

/**
 * Male-only step inserts for shared treatments. Each entry is positioned
 * relative to a canonical step by `insertAfter` (step id). When the named
 * step is absent or the property is omitted, the insert is appended at the
 * end of the flow.
 *
 * Step IDs must still match Doctor Dashboard API phase types — reuse
 * existing IDs where possible (e.g. `sperm_collection` is the same id used
 * inline by the sperm_freezing flow; here it is inserted after egg retrieval
 * for IVF and embryo freezing).
 */
type MalePartnerInsert = { insertAfter?: string; step: TreatmentStepConfig };
const MALE_PARTNER_STEPS: Partial<Record<CycleType, MalePartnerInsert[]>> = {
  ivf: [{ insertAfter: "egg_retrieval", step: SPERM_COLLECTION_STEP }],
};

/** Splice inserts into a step list after their `insertAfter` anchor (or append). */
function applyInserts(
  steps: TreatmentStepConfig[],
  inserts: MalePartnerInsert[] | undefined,
  cycleType: CycleType,
  t?: TranslationFunction,
): TreatmentStepConfig[] {
  if (!inserts || inserts.length === 0) return steps;
  const result = [...steps];
  for (const { insertAfter, step } of inserts) {
    const translated = t ? translateStep(step, cycleType, t) : step;
    const anchorIdx =
      insertAfter !== undefined
        ? result.findIndex((s) => s.id === insertAfter)
        : -1;
    if (anchorIdx >= 0) {
      result.splice(anchorIdx + 1, 0, translated);
    } else {
      result.push(translated);
    }
  }
  return result;
}

/**
 * Returns the merged step list for a treatment, applying partner-perspective
 * overrides when the viewer is the male partner of the primary (AFAB)
 * patient.
 *
 * - `self` perspective (or non-shared treatments): canonical TREATMENT_FLOWS
 *   entries with no overrides.
 * - `partner` perspective: `step.partnerCopy` strings replace `title`,
 *   `description`, `prompt` per-step; any male-only `MALE_PARTNER_STEPS` are
 *   spliced in after their `insertAfter` anchor step (or appended).
 *
 * Step IDs never change — the API phase mapping stays intact.
 */
/**
 * Give a flow that ends in a result its terminal Outcome step.
 *
 * Appended here rather than written into all five flow literals so the two
 * cannot drift, and so a preservation flow — which has no result to wait for —
 * cannot accidentally acquire one. The luteal phase is what identifies a flow
 * that ends this way.
 */
function withOutcomeStep(steps: TreatmentStepConfig[]): TreatmentStepConfig[] {
  if (!steps.some((step) => step.id === "luteal_phase")) return steps;
  if (steps.some((step) => step.id === OUTCOME_STEP.id)) return steps;
  return [...steps, OUTCOME_STEP];
}

export function getStepsForPerspective(
  cycleType: CycleType,
  perspective: StepPerspective,
  t?: TranslationFunction,
): TreatmentStepConfig[] {
  const rawBase = withOutcomeStep(TREATMENT_FLOWS[cycleType] ?? []);
  const translatedBase = t
    ? rawBase.map((s) => translateStep(s, cycleType, t))
    : rawBase;
  if (perspective === "self") return translatedBase;

  const base = translatedBase;
  const overridden = base.map((step) => {
    const c = step.partnerCopy;
    if (!c) return step;
    const merged: TreatmentStepConfig = {
      ...step,
      title: c.title ?? step.title,
      description: c.description ?? step.description,
      prompt: c.prompt ?? step.prompt,
      skipText: c.skipText ?? step.skipText,
    };
    if (c.fields && step.fields) {
      const byName = new Map(c.fields.map((f) => [f.name, f]));
      merged.fields = step.fields.map((f) => byName.get(f.name) ?? f);
      // Append partner-only fields not present in canonical
      for (const pf of c.fields) {
        if (!step.fields.some((sf) => sf.name === pf.name)) {
          merged.fields.push(pf);
        }
      }
    } else if (c.fields) {
      merged.fields = c.fields;
    }
    return merged;
  });

  return applyInserts(overridden, MALE_PARTNER_STEPS[cycleType], cycleType, t);
}

export type ConditionalCycleOptions = {
  protocol?: Cycle["protocol"];
  phases?: ConditionalCyclePhase[];
  pgtPlan?: PgtPlan | null;
  cyclePlanSnapshot?: Cycle["cyclePlanSnapshot"];
};

/**
 * Resolve the patient-facing graph from server-backed cycle facts.
 *
 * When a cycle has an active server plan snapshot, it is the source of truth
 * for the IUI/OI step list. Existing phase-derived behavior remains the
 * compatibility path for cycles created before plan snapshots were wired into
 * Start Treatment.
 */
export function getStepsForCycle(
  cycleType: CycleType,
  perspective: StepPerspective,
  t?: TranslationFunction,
  options: ConditionalCycleOptions = {},
): TreatmentStepConfig[] {
  let steps = getStepsForPerspective(cycleType, perspective, t);
  const phases = options.phases ?? [];
  const cyclePlanFlow = resolveCyclePlanFlow(options.cyclePlanSnapshot);
  const graph = resolveConditionalCycleGraph(
    cycleType,
    options.protocol ?? null,
    phases,
    options.pgtPlan,
  );

  if (cyclePlanFlow) {
    const definitions = new Map(steps.map((step) => [step.id, step]));
    steps = cyclePlanFlow.steps.flatMap((planStep) => {
      const step = definitions.get(planStep.id);
      return step ? [step] : [];
    });

    steps = steps.map((step) =>
      step.id === "protocol"
        ? {
            ...step,
            hasTriggerShot: cyclePlanFlow.hasTriggerOrSurge,
          }
        : step,
    );

    // A server plan snapshot lists the clinic's steps, and the outcome is not one
    // of them — it is the cycle's result, not a phase the clinic schedules. Taken
    // literally, the rebuild above would drop the Outcome tab from exactly the
    // cycles that have a plan. Re-attached from the translated definitions.
    const outcomeStep = definitions.get(OUTCOME_STEP.id);
    if (outcomeStep) {
      steps = withOutcomeStep(steps).map((step) =>
        step.id === OUTCOME_STEP.id ? outcomeStep : step,
      );
    }
  } else if (graph.includePgt) {
    steps = applyInserts(
      steps,
      [
        {
          insertAfter: "embryo_development",
          step: PGT_STEP,
        },
      ],
      cycleType,
      t,
    );
  }

  if (!cyclePlanFlow && graph.omitProtocol) {
    steps = steps.filter((step) => step.id !== "protocol");
  }

  if (!cyclePlanFlow && graph.includeInsemination) {
    const lutealIndex = steps.findIndex((step) => step.id === "luteal_phase");
    const procedureSteps = [SPERM_COLLECTION_STEP, INSEMINATION_STEP];
    steps =
      lutealIndex >= 0
        ? [
            ...steps.slice(0, lutealIndex),
            ...procedureSteps,
            ...steps.slice(lutealIndex),
          ]
        : [...steps, ...procedureSteps];
  }

  if (cycleType === "embryo_freezing") {
    steps = insertRequiredStep(
      steps,
      "fertilization",
      t
        ? translateStep(EMBRYO_DEVELOPMENT_STEP, cycleType, t)
        : EMBRYO_DEVELOPMENT_STEP,
    );
    steps = insertServerBackedSteps(steps, phases, [
      {
        phaseType: "sperm_collection",
        insertAfter: "egg_retrieval",
        step: t
          ? translateStep(SPERM_COLLECTION_STEP, cycleType, t)
          : SPERM_COLLECTION_STEP,
      },
    ]);
  }

  if (!cyclePlanFlow && (cycleType === "iui" || cycleType === "oi")) {
    steps = steps.map((step) =>
      step.id === "protocol"
        ? {
            ...step,
            hasTriggerShot: graph.hasTriggerData,
          }
        : step,
    );
  }

  return steps;
}
